import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Card, Typography, Space, Button, message, Row, Col, DatePicker } from 'antd';
import { LogoutOutlined, LeftOutlined, RightOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AnalyticsI18nProvider, useI18n } from './i18n';
import StatWidget from './components/StatWidget';
import AreaChart from './components/AreaChart';
import IsLoading from '../../shared/components/IsLoading';
import ErrorPage from '../../shared/components/ErrorPage';
import {
    useAnalyticsChart,
    useAnalyticsRevenueChart
} from './hooks';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AnalyticsInner = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const { t, lang, setLang } = useI18n();

    // 기간 선택 (기본: 오늘)
    const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
    const [country, setCountry] = useState('ALL');
    const [showScrollTop, setShowScrollTop] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 스크롤 감지 (Scroll to Top 버튼용)
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 날짜 이동 함수들
    const moveDateRange = (days) => {
        setDateRange([
            dateRange[0].add(days, 'day'),
            dateRange[1].add(days, 'day')
        ]);
    };

    // 다음날 버튼 비활성화 여부 (종료일이 오늘 이상이면 비활성화)
    const isNextDisabled = dateRange[1] && dateRange[1].startOf('day').isSame(dayjs().startOf('day')) ||
                           dateRange[1] && dateRange[1].isAfter(dayjs().startOf('day'));

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        // 인증 확인
        const isAuth = sessionStorage.getItem('analytics_auth');
        if (!isAuth) {
            message.warning(t('needAuth'));
            window.location.href = '/analytics/auth';
        }
    }, [lang, t]);

    // 데이터 훅스
    const dauQuery = useAnalyticsChart('dau', dateRange, country);
    const mauQuery = useAnalyticsChart('mau', dateRange, country);
    const dnuQuery = useAnalyticsChart('dnu', dateRange, country);
    const puQuery = useAnalyticsChart('pu', dateRange, country);
    const totalQuery = useAnalyticsChart('total', dateRange, country);
    const revenueQuery = useAnalyticsChart('revenue', dateRange, country);

    const chartsLoading = dauQuery.loading || mauQuery.loading || dnuQuery.loading || puQuery.loading || totalQuery.loading || revenueQuery.loading;

    // ARPU와 ARPPU 차트 계산
    const { arpuChart, arppuChart, arpuPeriodAverage, arppuPeriodAverage, arpuMonthlyStats, arppuMonthlyStats } = useMemo(() => {
        const dauChart = dauQuery.rows || [];
        const puChart = puQuery.rows || [];
        const revenueChart = revenueQuery.rows || [];

        const calcRatioChart = (aChart, bChart) => {
            const aMap = Object.fromEntries(aChart.map(item => [item.keyName, item.Total]));
            const bMap = Object.fromEntries(bChart.map(item => [item.keyName, item.Total]));

            const allDates = Array.from(new Set([
                ...aChart.map(item => item.keyName),
                ...bChart.map(item => item.keyName)
            ])).sort();

            return allDates.map(date => {
                const a = Number(aMap[date]) || 0;
                const b = Number(bMap[date]) || 0;
                return {
                    keyName: date,
                    Total: a > 0 ? b / a : 0
                };
            });
        };

        const arpu = calcRatioChart(dauChart, revenueChart);
        const arppu = calcRatioChart(puChart, revenueChart);

        // 기간 평균 계산
        const arpuAvg = dauQuery.periodTotal > 0 ? revenueQuery.periodTotal / dauQuery.periodTotal : 0;
        const arppuAvg = puQuery.periodTotal > 0 ? revenueQuery.periodTotal / puQuery.periodTotal : 0;

        // 월별 평균 계산
        const calcMonthlyAverage = (baseStats, revenueStats) => {
            if (!baseStats || !revenueStats) return [];

            const monthMap = {};
            revenueStats.forEach(stat => {
                monthMap[stat.month] = stat.average;
            });

            return baseStats.map(stat => {
                const revenueAvg = monthMap[stat.month] || 0;
                const average = stat.average > 0 ? revenueAvg / stat.average : 0;
                return {
                    month: stat.month,
                    average: average
                };
            });
        };

        const arpuMonthly = calcMonthlyAverage(dauQuery.monthlyStats, revenueQuery.monthlyStats);
        const arppuMonthly = calcMonthlyAverage(puQuery.monthlyStats, revenueQuery.monthlyStats);

        return {
            arpuChart: arpu,
            arppuChart: arppu,
            arpuPeriodAverage: arpuAvg,
            arppuPeriodAverage: arppuAvg,
            arpuMonthlyStats: arpuMonthly,
            arppuMonthlyStats: arppuMonthly
        };
    }, [dauQuery.rows, dauQuery.periodTotal, dauQuery.monthlyStats, puQuery.rows, puQuery.periodTotal, puQuery.monthlyStats, revenueQuery.rows, revenueQuery.periodTotal, revenueQuery.monthlyStats]);

    // 차트 스크롤용 ref들
    const chartRefs = {
        dnu: useRef(null),
        dau: useRef(null),
        mau: useRef(null),
        pu: useRef(null),
        revenue: useRef(null),
        arpu: useRef(null),
        arppu: useRef(null),
    };

    const scrollToChart = (key) => {
        const el = chartRefs[key]?.current;
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 12;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    const handleLogout = () => {
        sessionStorage.removeItem('analytics_auth');
        message.success(t('logoutSuccess'));
        window.location.href = '/analytics/auth';
    };

    const firstError = dauQuery.error || mauQuery.error || dnuQuery.error || puQuery.error || revenueQuery.error;

    if (firstError) {
        return <ErrorPage error={firstError} />;
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: '#f5f5f5',
            padding: isMobile ? '0' : '20px',
        }}>
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                background: 'white',
                borderRadius: isMobile ? '0' : '16px',
                overflow: 'hidden',
                boxShadow: isMobile ? 'none' : '0 4px 20px rgba(0,0,0,0.1)',
            }}>
                {chartsLoading && (
                    <div style={{ padding: isMobile ? '8px 16px' : '12px 24px' }}>
                        <IsLoading />
                    </div>
                )}
                {/* Header */}
                <Card
                    style={{
                        borderRadius: isMobile ? '0' : '16px 16px 0 0',
                        border: 'none',
                        borderBottom: '1px solid #f0f0f0',
                        padding: isMobile ? '16px' : '20px 24px',
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <div>
                            <Title level={isMobile ? 4 : 3} style={{ margin: '0 0 4px 0' }}>
                                {t('title')}
                            </Title>
                            <Text type="secondary">{t('subtitle')}</Text>
                        </div>
                        <Space>
                            <select
                                value={lang}
                                onChange={(e) => setLang(e.target.value)}
                                style={{ width: 110, height: 32, borderRadius: 6, padding: '0 8px' }}
                                aria-label="language"
                            >
                                <option value="ko">한국어</option>
                                <option value="ja">日本語</option>
                            </select>
                            <Button
                                type="text"
                                icon={<LogoutOutlined />}
                                onClick={handleLogout}
                                style={{ color: '#ff4d4f' }}
                            >
                                {!isMobile && t('logout')}
                            </Button>
                        </Space>
                    </div>
                </Card>

                {/* Controls */}
                <div style={{ padding: isMobile ? '0 16px 16px' : '0 24px 24px' }}>
                    <Card style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Space direction={isMobile ? 'vertical' : 'horizontal'} size="middle" style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: 'flex-end' }}>
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('range')}</Text>
                                <Space>
                                    <RangePicker value={dateRange} onChange={setDateRange} />
                                    <Button.Group>
                                        <Button
                                            icon={<LeftOutlined />}
                                            onClick={() => moveDateRange(-1)}
                                        />
                                        <Button onClick={() => setDateRange([dayjs(), dayjs()])}>
                                            {t('today')}
                                        </Button>
                                        <Button
                                            icon={<RightOutlined />}
                                            onClick={() => moveDateRange(1)}
                                            disabled={isNextDisabled}
                                        />
                                    </Button.Group>
                                </Space>
                            </div>
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('country')}</Text>
                                <select
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    style={{ width: 160, height: 32, borderRadius: 6, padding: '0 8px' }}
                                    aria-label="country"
                                >
                                    <option value="ALL">{t('country_all')}</option>
                                    <option value="JP">{t('country_JP')}</option>
                                    <option value="KR">{t('country_KR')}</option>
                                    <option value="US">{t('country_US')}</option>
                                    <option value="TH">{t('country_TH')}</option>
                                    <option value="TW">{t('country_TW')}</option>
                                    <option value="CN">{t('country_CN')}</option>
                                    <option value="HK">{t('country_HK')}</option>
                                    <option value="ETC">{t('country_ETC')}</option>
                                </select>
                            </div>
                        </Space>
                    </Card>
                </div>

                {/* Widgets */}
                <div style={{ padding: isMobile ? '0 16px 16px' : '0 24px 24px' }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} lg={6}>
                            <StatWidget
                                labelKey="dnu"
                                value={dnuQuery.periodTotal}
                                unitType="people"
                                accent="#52c41a"
                                isLoading={dnuQuery.loading}
                                onClick={() => scrollToChart('dnu')}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatWidget
                                labelKey="dau"
                                value={dauQuery.periodAverage}
                                unitType="people"
                                accent="#722ed1"
                                isLoading={dauQuery.loading}
                                onClick={() => scrollToChart('dau')}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatWidget
                                labelKey="mau"
                                value={mauQuery.periodAverage}
                                unitType="people"
                                accent="#1890ff"
                                isLoading={mauQuery.loading}
                                onClick={() => scrollToChart('mau')}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatWidget
                                labelKey="Total"
                                value={totalQuery.periodTotal}
                                unitType="people"
                                accent="#1890ff"
                                isLoading={totalQuery.loading}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatWidget
                                labelKey="pu"
                                value={puQuery.periodTotal}
                                unitType="people"
                                accent="#13c2c2"
                                isLoading={puQuery.loading}
                                onClick={() => scrollToChart('pu')}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatWidget
                                labelKey="revenue"
                                value={revenueQuery.periodTotal}
                                unitType="usd"
                                accent="#fa8c16"
                                isLoading={revenueQuery.loading}
                                onClick={() => scrollToChart('revenue')}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatWidget
                                labelKey="arpu"
                                value={arpuPeriodAverage}
                                unitType="usd"
                                accent="#eb2f96"
                                isLoading={dauQuery.loading || revenueQuery.loading}
                                onClick={() => scrollToChart('arpu')}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatWidget
                                labelKey="arppu"
                                value={arppuPeriodAverage}
                                unitType="usd"
                                accent="#2f54eb"
                                isLoading={puQuery.loading || revenueQuery.loading}
                                onClick={() => scrollToChart('arppu')}
                            />
                        </Col>
                    </Row>
                </div>

                {/* Charts */}
                <div style={{ padding: isMobile ? '0 16px 24px' : '0 24px 32px' }}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div ref={chartRefs.dnu}>
                            <AreaChart
                                titleKey="dnu"
                                data={dnuQuery.rows}
                                monthlyStats={dnuQuery.monthlyStats}
                                dataKey="Total"
                                color="#52c41a"
                                isLoading={chartsLoading}
                            />
                        </div>
                        <div ref={chartRefs.dau}>
                            <AreaChart
                                titleKey="dau"
                                data={dauQuery.rows}
                                monthlyStats={dauQuery.monthlyStats}
                                dataKey="Total"
                                color="#722ed1"
                                isLoading={chartsLoading}
                                showTotal={false}
                            />
                        </div>
                        <div ref={chartRefs.mau}>
                            <AreaChart
                                titleKey="mau"
                                data={mauQuery.rows}
                                monthlyStats={[]}
                                dataKey="Total"
                                color="#1890ff"
                                isLoading={chartsLoading}
                            />
                        </div>
                        <div ref={chartRefs.pu}>
                            <AreaChart
                                titleKey="pu"
                                data={puQuery.rows}
                                monthlyStats={puQuery.monthlyStats}
                                dataKey="Total"
                                color="#13c2c2"
                                isLoading={chartsLoading}
                            />
                        </div>
                        <div ref={chartRefs.revenue}>
                            <AreaChart
                                titleKey="revenue"
                                data={revenueQuery.rows}
                                monthlyStats={revenueQuery.monthlyStats}
                                unitType="usd"
                                dataKey="Total"
                                color="#fa8c16"
                                isLoading={chartsLoading}
                            />
                        </div>
                        <div ref={chartRefs.arpu}>
                            <AreaChart
                                titleKey="arpu"
                                data={arpuChart}
                                monthlyStats={arpuMonthlyStats}
                                unitType="usd"
                                dataKey="Total"
                                color="#eb2f96"
                                isLoading={chartsLoading}
                                showTotal={false}
                            />
                        </div>
                        <div ref={chartRefs.arppu}>
                            <AreaChart
                                titleKey="arppu"
                                data={arppuChart}
                                monthlyStats={arppuMonthlyStats}
                                unitType="usd"
                                dataKey="Total"
                                color="#2f54eb"
                                isLoading={chartsLoading}
                                showTotal={false}
                            />
                        </div>
                    </Space>
                </div>
            </div>

            {/* Scroll to Top Button */}
            {showScrollTop && (
                <Button
                    type="primary"
                    shape="circle"
                    icon={<VerticalAlignTopOutlined />}
                    onClick={scrollToTop}
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        width: 48,
                        height: 48,
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                />
            )}
        </div>
    );
};

const Analytics = () => (
    <AnalyticsI18nProvider>
        <AnalyticsInner />
    </AnalyticsI18nProvider>
);

export default Analytics;